import React, {useContext, useEffect} from 'react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {OktaAuth, OktaAuthOptions} from '@okta/okta-auth-js';
import {Security} from '@okta/okta-react';

import {setupSessionInterceptor} from './utils/axios-session-interceptor';

// This CSS file can be used to override the FAA default theming
import './App.css';
import HeaderComponent from './components/Header.component';

import {routes as appRoutes} from './routes';
import AuthService from './services/auth.service';
import {UserContext} from './context/userContext';
import ProtectedRoute from './components/ProtectedRoute';
import {ProfilerPanel} from './components/profiler';

setupSessionInterceptor();

function App(): JSX.Element {

    // Get USE_OKTA from .env file
    const USE_OKTA = process.env.REACT_APP_USE_OKTA === 'true';

    // Get the saveOktaAuthOptions function from UserContext if USE_OKTA is true.
    const { saveOktaAuthOptions } = useContext(UserContext);
    const [oktaAuth, setOktaAuth] = React.useState<OktaAuth>();
    const restoreOriginalUri = async (oktaAuth: OktaAuth, originalUri: string) : Promise<void> => {
        // Store the original URI for the LoginCallback component to use after authentication
        if (originalUri) {
            // Store it on the oktaAuth instance so LoginCallback can access it
            oktaAuth.setOriginalUri(originalUri);
        }

        // DO NOT navigate here! Let the LoginCallback component handle navigation
        // after it completes the backend JWT exchange and saves the user.
        // This prevents the race condition where we navigate before authentication is complete.
    };

    // Occurs ONCE, AFTER the initial render.
    useEffect(() => {
        if (USE_OKTA && oktaAuth === undefined) {
            AuthService.fetchOktaProperties().then((response) => {
                const oktaProps: OktaAuthOptions = {
                    clientId: response.data.clientId,
                    issuer: response.data.issuer,
                    redirectUri: response.data.redirectUri,
                    scopes: response.data.scopes,
                    responseMode: response.data.responseMode,
                    pkce: response.data.pkce,
                    tokenManager: {
                        autoRenew: false
                    }
                }
                saveOktaAuthOptions(oktaProps);
                setOktaAuth(new OktaAuth(oktaProps));
            }).catch((e: any) => {
                alert('Okta is enabled but the platform is unable to connect to the Okta services. ' + e);
            });
        }
    }, [USE_OKTA, oktaAuth, saveOktaAuthOptions]);

    /**
     * This function returns the main content of the application wrapped in a BrowserRouter.
     * It includes the HeaderComponent and the main Routes for the application.
     * @returns {JSX.Element} The main content of the application.
     */
    function routerContent(): JSX.Element {
        return (
            <BrowserRouter basename={'/isam'} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div style={{display: 'inline-block', width: '100%', verticalAlign: 'top'}}>
                    <HeaderComponent/>
                </div>
                <main className={'devkit-main-wrapper'} id={'main-content'}>
                    <Routes>
                        {appRoutes.map((route) => {
                            if (!route.component) return null;

                            // Wrap protected routes with ProtectedRoute component
                            if (route.requiresAuth) {
                                return (
                                    <Route
                                        key={route.key}
                                        path={route.path}
                                        element={
                                            <ProtectedRoute>
                                                <route.component/>
                                            </ProtectedRoute>
                                        }
                                    />
                                );
                            }

                            // Non-protected routes render directly
                            return (
                                <Route
                                    key={route.key}
                                    path={route.path}
                                    element={<route.component/>}
                                />
                            );
                        })}
                    </Routes>
                </main>
            </BrowserRouter>
        );
    }

    return (
        <div className='App'>
            <ProfilerPanel />
            {USE_OKTA ? (
                oktaAuth !== undefined ? (
                    <Security
                        oktaAuth={oktaAuth}
                        // Removed onAuthRequired to prevent race condition with ProtectedRoute
                        // ProtectedRoute component handles authentication checking and redirects
                        restoreOriginalUri={restoreOriginalUri}
                    >
                        {routerContent()}
                    </Security>
                ) : <p>Please wait while the application loads...</p>
            ) : (
                routerContent()
            )}
        </div>
    );
}

export default App;
